/**
 * Typed API client for the Collective Selection endpoints.
 *
 * The selection module routes through the authed Express API (not browser-direct
 * DAL like Programs), because ballot blindness + audit live server-side. These
 * wrappers unwrap the `{ data }` envelope the API returns and reuse the shared
 * DAL types so the UI and server agree on shapes.
 */

import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './api';
import type {
  Selection,
  SelectionUpdate,
  SelectionVoter,
  SelectionRecusal,
  SelectionRound,
  SelectionBallotWithEntries,
  BallotEntryInput,
  RoundKind,
  RoundState,
  CutoffRule,
  TallyRow,
  SelectionIndividualPick,
} from '@littlebooker/dal';

// owner_id is optional here — the API defaults it to the acting user.
export interface SelectionCreateInput {
  title: string;
  description?: string | null;
  target_pct?: number | null;
  scope?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  owner_id?: string;
  state?: 'draft' | 'active' | 'complete';
}

export interface CandidateArtist {
  person_id: string;
  name: string;
  slug: string | null;
  image_url: string | null;
}

export interface CandidateArchiveLink {
  type: string;
  url: string;
  name?: string;
}

// Candidate happenings come back shaped by the API with joined artists and an
// `excluded` flag (whether the owner removed it from scope).
export interface CandidateHappening {
  id: string;
  slug: string;
  title: string;
  type: string;
  start_time: string | null;
  status: string;
  is_public: boolean;
  image_url: string | null;
  episode_number: number | null;
  archive_links: CandidateArchiveLink[];
  is_staff_pick?: boolean;
  artists: CandidateArtist[];
  excluded: boolean;
  [key: string]: unknown;
}

export interface SelectionBundle {
  selection: Selection;
  voters: SelectionVoter[];
  recusals: SelectionRecusal[];
  rounds: SelectionRound[];
}

export interface MaxPicksSuggestion {
  suggested_max_picks: number;
  scope_size: number;
  target_pct: number;
  target_count: number;
  voter_count: number;
  note: string;
}

type Envelope<T> = { data: T; count?: number };

export async function listSelections(params?: { state?: string; query?: string }): Promise<Selection[]> {
  const search = new URLSearchParams();
  if (params?.state) search.set('state', params.state);
  if (params?.query) search.set('query', params.query);
  const qs = search.toString();
  const res = await apiGet<Envelope<Selection[]>>(`/api/v1/selections${qs ? `?${qs}` : ''}`);
  return res.data;
}

export async function getSelectionBundle(id: string): Promise<SelectionBundle> {
  const res = await apiGet<Envelope<SelectionBundle>>(`/api/v1/selections/${id}`);
  return res.data;
}

export async function getCandidates(id: string): Promise<CandidateHappening[]> {
  const res = await apiGet<Envelope<CandidateHappening[]>>(`/api/v1/selections/${id}/candidates`);
  return res.data;
}

export async function getMaxPicksSuggestion(
  id: string,
  params?: { target_pct?: number; voter_count?: number },
): Promise<MaxPicksSuggestion> {
  const search = new URLSearchParams();
  if (params?.target_pct !== undefined) search.set('target_pct', String(params.target_pct));
  if (params?.voter_count !== undefined) search.set('voter_count', String(params.voter_count));
  const qs = search.toString();
  const res = await apiGet<Envelope<MaxPicksSuggestion>>(
    `/api/v1/selections/${id}/suggest/max-picks${qs ? `?${qs}` : ''}`,
  );
  return res.data;
}

export async function createSelection(input: SelectionCreateInput): Promise<Selection> {
  const res = await apiPost<Envelope<Selection>>('/api/v1/selections', input);
  return res.data;
}

export async function updateSelection(id: string, input: SelectionUpdate): Promise<Selection> {
  const res = await apiPut<Envelope<Selection>>(`/api/v1/selections/${id}`, input);
  return res.data;
}

export async function deleteSelection(id: string): Promise<void> {
  await apiDelete(`/api/v1/selections/${id}`);
}

export async function setVoters(id: string, userIds: string[]): Promise<SelectionVoter[]> {
  const res = await apiPut<Envelope<SelectionVoter[]>>(`/api/v1/selections/${id}/voters`, {
    user_ids: userIds,
  });
  return res.data;
}

export async function addRecusal(
  id: string,
  input: { user_id: string; happening_id: string; reason?: string | null },
): Promise<SelectionRecusal> {
  const res = await apiPost<Envelope<SelectionRecusal>>(`/api/v1/selections/${id}/recusals`, input);
  return res.data;
}

export async function removeRecusal(id: string, recusalId: string): Promise<void> {
  await apiDelete(`/api/v1/selections/${id}/recusals/${recusalId}`);
}

// ---------- rounds ----------

export async function listRounds(id: string): Promise<SelectionRound[]> {
  const res = await apiGet<Envelope<SelectionRound[]>>(`/api/v1/selections/${id}/rounds`);
  return res.data;
}

export async function createRound(
  id: string,
  input: { kind: RoundKind; config?: Record<string, unknown>; opens_at?: string | null; closes_at?: string | null },
): Promise<SelectionRound> {
  const res = await apiPost<Envelope<SelectionRound>>(`/api/v1/selections/${id}/rounds`, input);
  return res.data;
}

export async function updateRound(
  id: string,
  roundId: string,
  patch: { config?: Record<string, unknown>; opens_at?: string | null; closes_at?: string | null },
): Promise<SelectionRound> {
  const res = await apiPatch<Envelope<SelectionRound>>(
    `/api/v1/selections/${id}/rounds/${roundId}`,
    patch,
  );
  return res.data;
}

export async function transitionRound(
  id: string,
  roundId: string,
  to: RoundState,
): Promise<SelectionRound> {
  const res = await apiPost<Envelope<SelectionRound>>(
    `/api/v1/selections/${id}/rounds/${roundId}/transition`,
    { to },
  );
  return res.data;
}

// ---------- ballots (acting voter) ----------

export async function getBallot(
  id: string,
  roundId: string,
): Promise<SelectionBallotWithEntries | null> {
  const res = await apiGet<Envelope<SelectionBallotWithEntries | null>>(
    `/api/v1/selections/${id}/rounds/${roundId}/ballot`,
  );
  return res.data;
}

export async function saveBallot(
  id: string,
  roundId: string,
  entries: BallotEntryInput[],
): Promise<SelectionBallotWithEntries> {
  const res = await apiPut<Envelope<SelectionBallotWithEntries>>(
    `/api/v1/selections/${id}/rounds/${roundId}/ballot`,
    { entries },
  );
  return res.data;
}

export async function submitBallotApi(
  id: string,
  roundId: string,
  entries: BallotEntryInput[],
): Promise<SelectionBallotWithEntries> {
  const res = await apiPost<Envelope<SelectionBallotWithEntries>>(
    `/api/v1/selections/${id}/rounds/${roundId}/ballot/submit`,
    { entries },
  );
  return res.data;
}

export interface RoundProgress {
  expected: number;
  submitted: number;
  submittedVoterIds: string[];
  draftVoterIds: string[];
}

export async function getProgress(id: string, roundId: string): Promise<RoundProgress> {
  const res = await apiGet<Envelope<RoundProgress>>(
    `/api/v1/selections/${id}/rounds/${roundId}/progress`,
  );
  return res.data;
}

// ---------- reveal ----------

export interface CutoffPreview {
  ballotCount: number;
  nominatedCount: number;
  maxCount: number;
  distribution: Record<number, number>;
  wildcardCount: number;
  thresholdSizes: Record<number, number>;
  options: Array<{ rule: CutoffRule; size: number }>;
}

export async function getCutoffPreview(
  id: string,
  roundId: string,
  targetShortlist?: number,
): Promise<CutoffPreview> {
  const qs = targetShortlist ? `?target_shortlist=${targetShortlist}` : '';
  const res = await apiGet<Envelope<CutoffPreview>>(
    `/api/v1/selections/${id}/rounds/${roundId}/cutoff-preview${qs}`,
  );
  return res.data;
}

export async function revealRound(
  id: string,
  roundId: string,
  input: { rule: CutoffRule; include?: string[]; exclude?: string[]; reason?: string | null },
): Promise<{ shortlist: string[]; round: SelectionRound }> {
  const res = await apiPost<Envelope<{ shortlist: string[]; round: SelectionRound }>>(
    `/api/v1/selections/${id}/rounds/${roundId}/reveal`,
    input,
  );
  return res.data;
}

export async function recutShortlist(
  id: string,
  roundId: string,
  input: { rule: CutoffRule; include?: string[]; exclude?: string[]; reason?: string | null },
): Promise<{ shortlist: string[]; round: SelectionRound }> {
  const res = await apiPost<Envelope<{ shortlist: string[]; round: SelectionRound }>>(
    `/api/v1/selections/${id}/rounds/${roundId}/recut`,
    input,
  );
  return res.data;
}

/** Reveal an allocate round (discloses ballots; ranking comes from the tally). */
export async function revealAllocateRound(
  id: string,
  roundId: string,
): Promise<{ round: SelectionRound }> {
  const res = await apiPost<Envelope<{ round: SelectionRound }>>(
    `/api/v1/selections/${id}/rounds/${roundId}/reveal`,
    {},
  );
  return res.data;
}

export interface NominationTally {
  counts: Record<string, number>;
  wildcardHappeningIds: string[];
  ballotCount: number;
}

export async function getNominationCounts(id: string, roundId: string): Promise<NominationTally> {
  const res = await apiGet<Envelope<NominationTally>>(
    `/api/v1/selections/${id}/rounds/${roundId}/nominations`,
  );
  return res.data;
}

export interface RoundResults {
  rows: TallyRow[];
  ballotCount: number;
}

export async function getResults(id: string, roundId: string): Promise<RoundResults> {
  const res = await apiGet<Envelope<RoundResults>>(
    `/api/v1/selections/${id}/rounds/${roundId}/results`,
  );
  return res.data;
}

// ---------- individual picks ----------

export async function getIndividualPicks(id: string): Promise<SelectionIndividualPick[]> {
  const res = await apiGet<Envelope<SelectionIndividualPick[]>>(
    `/api/v1/selections/${id}/individual-picks`,
  );
  return res.data;
}

export async function setMyIndividualPick(
  id: string,
  input: { happening_id: string; justification?: string | null },
): Promise<SelectionIndividualPick> {
  const res = await apiPut<Envelope<SelectionIndividualPick>>(
    `/api/v1/selections/${id}/individual-picks/mine`,
    input,
  );
  return res.data;
}

export async function removeMyIndividualPick(id: string): Promise<void> {
  await apiDelete(`/api/v1/selections/${id}/individual-picks/mine`);
}
